import { Box, CardHeader, CardContent, Grid, FormHelperText, Stack, TextField, Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText, Chip, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import errorHandler from "../../../utils/errorHandler";
import CardLayout from "../../../layouts/CardLayout";
import { toast } from "react-toastify";
import { Close } from "@mui/icons-material";
import { removeSpaces } from "../../../utils/helpers";
import LoadingButton from "../../../components/Buttons/LoadingButton";
import { CmsSchema } from "../../../utils/Schema";
import { aboutUsService } from "../../../services/AllServices";
import EdiorJodit from '../../../components/editor/editorJodit';

const AboutUsPage = () => {
    const navigate = useNavigate();
    const [cmsData, setCmsData] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const id = "67c7fb985a0d2aa4f273dfb7";
    const pageType = id ? "Update" : "Add";
    const [options, setOptions] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await aboutUsService.getById({ id });
                setCmsData(response.data.data);
                setSelectedOptions(response.data.data.models || []);
            } catch (error) {
                errorHandler(error);
            }
        };
        if (id) fetchData();
    }, [id]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await aboutUsService.getOptions();
                setOptions(response.data.data.result);
            } catch (error) {
                errorHandler(error);
            }
        };
        fetchOptions();
    }, []);

    const handleSelectChange = (event) => {
        const value = event.target.value;
        if (value.length <= 4) {
            setSelectedOptions(value);
            formik.setFieldValue("models", value);
        } else {
            toast.error("You can select a maximum of 4 options.");
        }
    };

    const handleDelete = (id) => {
        const updatedOptions = selectedOptions.filter(item => item !== id);
        setSelectedOptions(updatedOptions);
        formik.setFieldValue("models", updatedOptions);
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: cmsData?.title || "",
            ourMission: cmsData?.ourMission || "",
            ourVission: cmsData?.ourVission || "",
            description: cmsData?.description || "",
            models: cmsData?.models || [],
        },
        validationSchema: CmsSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
                const valueData = removeSpaces({ ...values, id });
                const resp = await aboutUsService.updateAboutUs(valueData);
                resp.data.status ? toast.success(resp.data.message) : toast.error(resp.data.message);
                // navigate(-1);
            } catch (error) {
                errorHandler(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Box>
            <Helmet>
                <title>{pageType} CMS</title>
            </Helmet>
            <CardLayout>
                <CardHeader title={`${pageType} CMS`} />
                <CardContent>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                            <Grid container spacing={3} direction="column">
                                <FormField name="title" label="Title" formik={formik} />

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Select Options</InputLabel>
                                        <Select
                                            multiple
                                            value={selectedOptions}
                                            onChange={handleSelectChange}
                                            renderValue={(selected) => (
                                                <div>
                                                    {selected?.map((id) => {
                                                        const option = options.find((opt) => opt._id === id);
                                                        return (
                                                            <Chip
                                                                key={id}
                                                                label={option ? option.username : id}
                                                                onDelete={() => handleDelete(id)}
                                                                deleteIcon={<Close onMouseDown={(event) => event.stopPropagation()} />}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        >
                                            {options?.map((option) => (
                                                <MenuItem key={option._id} value={option._id}>
                                                    <Checkbox checked={selectedOptions.includes(option._id)} />
                                                    <ListItemText primary={option.username} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <label>Our Mission</label>
                                    <br /><br />
                                    <EdiorJodit
                                        formik={formik}
                                        fieldName="ourMission"
                                        onChange={(val) => formik.setFieldValue("ourMission", val)}
                                        data={formik.values.ourMission}
                                    />
                                    {formik.touched.ourMission && formik.errors.ourMission && (
                                        <FormHelperText error>{formik.errors.ourMission}</FormHelperText>
                                    )}
                                    <br /><br />
                                </Grid>

                                <Grid item xs={12}>
                                    <label>Our Vision</label>
                                    <br /><br />
                                    <EdiorJodit
                                        formik={formik}
                                        fieldName="ourVission"
                                        onChange={(val) => formik.setFieldValue("ourVission", val)}
                                        data={formik.values.ourVission}
                                    />
                                    {formik.touched.ourVission && formik.errors.ourVission && (
                                        <FormHelperText error>{formik.errors.ourVission}</FormHelperText>
                                    )}
                                    <br /><br />
                                </Grid>

                                <Grid item xs={12}>
                                    <label>Description</label>
                                    <br /><br />
                                    <EdiorJodit
                                        formik={formik}
                                        fieldName="description"
                                        onChange={(val) => formik.setFieldValue("description", val)}
                                        data={formik.values.description}
                                    />
                                    {formik.touched.description && formik.errors.description && (
                                        <FormHelperText error>{formik.errors.description}</FormHelperText>
                                    )}
                                </Grid>

                            </Grid>

                            <Stack spacing={2} mt={3} direction="row" justifyContent="flex-end">
                                <LoadingButton text={pageType} loading={formik.isSubmitting} />
                            </Stack>

                        </Form>
                    </FormikProvider>
                </CardContent>
            </CardLayout>
        </Box>
    );
};

const FormField = React.memo(({ name, label, formik }) => (
    <Grid item xs={12}>
        <TextField
            fullWidth
            name={name}
            label={label}
            value={formik.values[name]}
            onChange={formik.handleChange}
            required
            error={Boolean(formik.touched[name] && formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
        />
    </Grid>
));

export default AboutUsPage;